/**
 * auth.ts — Cognito token handling. PURE LOGIC ONLY; the wiring lives in main.ts.
 *
 * Phase 1.3 of the serverless migration.
 *
 * WHY THIS FILE IS SEPARATE, again. `chartapp/README.md` records a ticket-state bug that
 * "passed all four gates green" because it lived in frontend state — pytest cannot see
 * `.ts`, and tsc/vite have nothing to object to in logic that is wrong but well-typed.
 * Gate 5 (vitest) is the only witness, and it can only see exported functions. So every
 * decision here is a pure function over explicit arguments: no DOM, no fetch, no
 * module-level mutable state. Same split as `aichat.ts`.
 *
 * TOKEN STORAGE: `localStorage`, CHANGED FROM `sessionStorage` ON 2026-07-31.
 *
 * This file previously said tokens must never reach `localStorage`, and predicted the exact
 * reversal that has now happened: "'use sessionStorage' is exactly the kind of decision a later
 * refactor reverses for convenience ('why do I keep logging in?')". Andrei asked for a one-month
 * session. So the reversal is deliberate, and the trade is stated rather than quietly made:
 *
 *   GIVEN UP — a token no longer dies with the tab. On a shared or stolen machine, the refresh
 *   token in `localStorage` is a month-long credential for reading his trading account. That is a
 *   real reduction in safety, not a neutral change.
 *
 *   GAINED — the session Andrei asked for. The alternative (keeping sessionStorage and re-logging
 *   in hourly) is not merely inconvenient: a user who logs in ten times a day types their password
 *   ten times, and that is its own exposure.
 *
 *   MITIGATIONS THAT MAKE THIS ACCEPTABLE, and none of them is optional:
 *     * only the REFRESH token is long-lived. The id/access tokens still expire in one hour, so a
 *       leaked id token is stale within the hour.
 *     * `signOut()` clears storage AND calls Cognito's global sign-out, so revocation is real and
 *       not just local forgetting.
 *     * MFA on the pool is the actual defence for a stolen refresh token, and it is still OPTIONAL
 *       — flagged to Andrei separately, because a month-long token without MFA is the weakest
 *       combination available here.
 *
 * The old test asserting `sessionStorage` is INVERTED rather than deleted, per this repo's rule for
 * assertions whose intent changes.
 *
 * THE EXPIRY CHECK IS LOCAL AND ADVISORY, NOT A SECURITY CONTROL.
 * `isExpired` reads the JWT's own `exp` claim to decide whether to bother sending a
 * request. It is a UX optimisation. The REAL check is server-side: API Gateway's JWT
 * authorizer verifies the signature, and nothing here can be trusted to gate access —
 * a token could be edited in devtools. This is stated because client-side expiry checks
 * are routinely mistaken for authorisation.
 *
 * NO SIGNATURE VERIFICATION HERE, deliberately. Verifying a JWT needs the pool's JWKS
 * and a crypto library; doing it in the browser would add weight and prove nothing the
 * server does not already prove. We decode the payload to read `exp` and `sub` for
 * display, and treat both as untrusted hints.
 */

/** What Cognito's InitiateAuth returns, narrowed to what this app uses. */
export type Tokens = {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
};

/** Claims we read for UI purposes only. Never for authorisation. */
export type Claims = {
  sub?: string;
  email?: string;
  exp?: number;
};

/**
 * Storage key. One key holding one JSON blob, so a partial write is impossible.
 *
 * The DEFAULT STORAGE is `localStorage` — see the header for why, and for what that costs. Every
 * function still takes `storage` explicitly so a test can pass a fake and so a future decision to
 * go back to per-tab sessions is a one-line change at the call sites, not a rewrite.
 */
const STORAGE_KEY = 'chartapp.auth';

/**
 * Decode a JWT payload WITHOUT verifying it.
 *
 * Returns null on anything malformed rather than throwing: a corrupt token must log the
 * user out cleanly, not crash the page on load. base64url needs `-`/`_` translated and
 * padding restored — `atob` alone rejects a real JWT.
 */
export function decodeClaims(token: string): Claims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    const claims = JSON.parse(json) as Claims;
    return typeof claims === 'object' && claims !== null ? claims : null;
  } catch {
    return null;
  }
}

/**
 * Is the token past its `exp`, or unreadable?
 *
 * `skewSeconds` treats a token as expired slightly EARLY. Without it, a token that
 * expires mid-flight produces a 401 the user reads as "the app is broken"; with it, the
 * client refreshes first. 30s covers ordinary clock drift.
 *
 * An UNREADABLE token counts as expired — fail closed, not open.
 */
export function isExpired(token: string | undefined | null, nowMs = Date.now(), skewSeconds = 30): boolean {
  if (!token) return true;
  const claims = decodeClaims(token);
  if (!claims || typeof claims.exp !== 'number') return true;
  return claims.exp * 1000 <= nowMs + skewSeconds * 1000;
}

/** The header every authenticated call carries. Empty when there is nothing usable. */
export function authHeader(tokens: Tokens | null, nowMs = Date.now()): Record<string, string> {
  // The ID token, not the access token: API Gateway's JWT authorizer validates the ID
  // token's `aud` against the client id, and `sub`/`email` claims are what the backend
  // uses to scope DynamoDB rows. Sending the access token here would authorise but not
  // identify.
  if (!tokens?.idToken || isExpired(tokens.idToken, nowMs)) return {};
  return { Authorization: `Bearer ${tokens.idToken}` };
}

/** True when there is a usable session — the only thing the UI should branch on. */
export function isSignedIn(tokens: Tokens | null, nowMs = Date.now()): boolean {
  return Boolean(tokens?.idToken) && !isExpired(tokens!.idToken, nowMs);
}

/**
 * Persist tokens for the tab's lifetime.
 *
 * Storage failures are SWALLOWED on purpose: Safari private mode throws on
 * `sessionStorage.setItem`, and a login that works but cannot cache should still leave
 * the user signed in for the current page rather than blowing up.
 */
export function saveTokens(tokens: Tokens, storage: Storage = localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    /* non-fatal: the in-memory copy still works for this page */
  }
}

/** Read tokens back, discarding anything unusable. */
export function loadTokens(storage: Storage = localStorage, nowMs = Date.now()): Tokens | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Tokens;
    if (!t?.idToken) return null;
    // An expired ID token with NO refresh token is dead weight — drop it so the UI
    // shows a login form instead of a signed-in shell that 401s on every call.
    if (isExpired(t.idToken, nowMs) && !t.refreshToken) return null;
    return t;
  } catch {
    return null;
  }
}

export function clearTokens(storage: Storage = localStorage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * The Cognito config the bundle needs, read from Vite env at BUILD time.
 *
 * NOT hardcoded and NOT fetched at runtime: the pipeline injects the real pool ids from
 * the CDK stack outputs, so a stack replacement cannot leave a stale id compiled into
 * the bundle. `configured()` is what the UI checks before offering a login form — an
 * unconfigured build should say so rather than fail obscurely inside the SDK.
 */
export type AuthConfig = {
  userPoolId: string;
  clientId: string;
  region: string;
};

export function authConfig(env: Record<string, string | undefined> = import.meta.env as never): AuthConfig {
  return {
    userPoolId: env.VITE_COGNITO_USER_POOL_ID ?? '',
    clientId: env.VITE_COGNITO_CLIENT_ID ?? '',
    region: env.VITE_AWS_REGION ?? 'us-east-1',
  };
}

export function configured(cfg: AuthConfig): boolean {
  // A pool id always contains the region and an underscore (`us-east-1_AbCdEfG`).
  // Checking the SHAPE rather than mere non-emptiness catches the common failure of a
  // build where the env var was set to a placeholder.
  return cfg.userPoolId.includes('_') && cfg.clientId.length > 0;
}

/**
 * Sign in with SRP and return the tokens.
 *
 * WHY `amazon-cognito-identity-js` RATHER THAN A HAND-ROLLED CALL. Cognito's SRP flow is
 * a multi-step challenge/response with a large-integer proof; implementing it by hand is
 * both error-prone and the kind of code that fails closed in production for reasons that
 * take a day to find. The alternative — `USER_PASSWORD_AUTH`, a single POST — would put
 * the plaintext password in a request body, and the pool deliberately does NOT enable
 * that flow (`authFlows: { userSrp: true }` only, asserted by a CDK test). Using the
 * official library keeps the strong flow AND avoids inventing crypto.
 *
 * NEW_PASSWORD_REQUIRED is surfaced rather than swallowed: an admin-created user lands in
 * FORCE_CHANGE_PASSWORD, and a login form that silently fails on the very first sign-in
 * of a brand-new account is the worst possible first impression. The caller gets a
 * distinct outcome and can prompt for a new password.
 *
 * The import is DYNAMIC so the ~90KB library is a separate chunk, loaded only when
 * someone actually signs in — the chart itself must not wait on it.
 */
export type SignInResult =
  | { kind: 'ok'; tokens: Tokens }
  | { kind: 'newPasswordRequired'; complete: (newPassword: string) => Promise<Tokens> }
  // TOTP CHALLENGE. Cognito issues this when the user HAS an enrolled authenticator. Without a
  // handler the login form falls through to its generic failure path — which is exactly why MFA
  // must not be set to REQUIRED before this exists: the pool would demand a factor the app cannot
  // answer, and the operator is locked out of their own app.
  | { kind: 'totpRequired'; complete: (code: string) => Promise<Tokens> }
  // MFA_SETUP CHALLENGE. Cognito issues this when the pool REQUIRES MFA and the user has nothing
  // enrolled. It arrives at first sign-in after enforcement is switched on, so it is handled here
  // rather than left to the profile screen — a user in this state cannot reach the profile screen.
  | { kind: 'mfaSetupRequired'; message: string }
  | { kind: 'error'; message: string };


/**
 * Exchange a refresh token for fresh id/access tokens.
 *
 * WHY THIS FUNCTION DID NOT EXIST AND HAD TO. `signIn` already SAVED
 * `session.getRefreshToken()`, and the pool has always issued 30-day refresh tokens
 * (`refreshTokenValidity: Duration.days(30)` in auth-stack.ts). But nothing ever CALLED refresh —
 * grep for `refreshSession` returned zero hits across the whole frontend. So the effective session
 * length was the ID token's one hour, and Andrei's "session time from login to automatic logout is
 * very short" was exactly right: the long-lived credential was being stored and ignored.
 *
 * That is the more useful half of this fix. Moving storage to `localStorage` only makes the token
 * SURVIVE a tab close; without a refresh call the session still died after an hour.
 *
 * Returns null rather than throwing on failure: an expired or revoked refresh token is the normal
 * end of a session, and the caller's job is then to show the login form, not to handle an error.
 */

// ───────────────────────────────────────────────────── TOTP enrolment (MFA)
//
// THE ORDER OF THESE THREE CALLS IS THE WHOLE PROTOCOL, and getting it wrong is how a user ends up
// with MFA "on" and no working factor:
//
//   1. associateSoftwareToken  -> Cognito returns a base32 secret. NOT yet enrolled.
//   2. verifySoftwareToken     -> proves the user's app generated a correct code from that secret.
//   3. setUserMfaPreference    -> only NOW does the factor count for sign-in.
//
// Skipping (3) leaves a verified-but-unpreferred factor: sign-in issues no challenge and the user
// believes they are protected when they are not. Skipping (2) and going straight to (3) enables a
// factor whose secret may never have reached an authenticator — that is the lockout.

/** A pending enrolment: the secret to show as a QR, and the verify step that completes it. */
export type TotpEnrolment = {
  /** Base32 secret. Shown as a QR (and as text, for a password manager). */
  secret: string;
  /** `otpauth://` URI — what the QR encodes. */
  uri: string;
  /** Verify a code from the authenticator, then make the factor PREFERRED. */
  verify: (code: string) => Promise<void>;
};

/**
 * Begin TOTP enrolment for the signed-in user.
 *
 * Needs a live session, so it takes the stored tokens: `CognitoUser` will not issue an authenticated
 * request without one, and refreshing here rather than failing keeps a month-old session usable for
 * enrolment (which is exactly when a user gets round to it).
 */
export async function beginTotpEnrolment(
  tokens: Tokens,
  label: string,
  cfg: AuthConfig = authConfig(),
): Promise<TotpEnrolment> {
  const { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } =
    await import('amazon-cognito-identity-js');

  const pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId });
  const sub = decodeClaims(tokens.idToken)?.sub;
  if (!sub) throw new Error('no session — sign in again before enrolling');
  const user = new CognitoUser({ Username: sub, Pool: pool });

  // The SDK's authenticated calls read the session off the user object, and a freshly constructed
  // CognitoUser has none. Setting it explicitly from stored tokens is what makes these calls work
  // outside the sign-in flow.
  user.setSignInUserSession(
    new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken ?? '' }),
    }),
  );

  const secret = await new Promise<string>((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: (code) => resolve(code),
      onFailure: (err) => reject(new Error(String(err?.message ?? err))),
    });
  });

  // `issuer` is what the authenticator app displays. It is URL-encoded because an unencoded space
  // silently truncates the label in some apps, leaving the user with an entry they cannot identify —
  // and an unidentifiable TOTP entry is one they will eventually delete.
  const issuer = encodeURIComponent('ChartApp');
  const account = encodeURIComponent(label);
  const uri = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}`;

  return {
    secret,
    uri,
    verify: async (code: string) => {
      await new Promise<void>((resolve, reject) => {
        user.verifySoftwareToken(code.trim(), 'authenticator', {
          onSuccess: () => resolve(),
          onFailure: (err) => reject(new Error(String(err?.message ?? err))),
        });
      });
      // STEP 3, and it is not optional — see the block comment above.
      await new Promise<void>((resolve, reject) => {
        user.setUserMfaPreference(
          null,
          { PreferredMfa: true, Enabled: true },
          (err) => (err ? reject(new Error(String(err?.message ?? err))) : resolve()),
        );
      });
    },
  };
}

/** Whether this user already has TOTP enabled, read from the live session rather than assumed. */
export async function totpEnabled(
  tokens: Tokens,
  cfg: AuthConfig = authConfig(),
): Promise<boolean> {
  const { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } =
    await import('amazon-cognito-identity-js');
  const pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId });
  const sub = decodeClaims(tokens.idToken)?.sub;
  if (!sub) return false;
  const user = new CognitoUser({ Username: sub, Pool: pool });
  user.setSignInUserSession(
    new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken ?? '' }),
    }),
  );
  return new Promise((resolve) => {
    user.getUserData((err, data) => {
      if (err || !data) {
        resolve(false);
        return;
      }
      resolve((data.UserMFASettingList ?? []).includes('SOFTWARE_TOKEN_MFA'));
    });
  });
}

export async function refreshTokens(
  tokens: Tokens,
  cfg: AuthConfig = authConfig(),
): Promise<Tokens | null> {
  if (!tokens.refreshToken) return null;
  const { CognitoRefreshToken, CognitoUser, CognitoUserPool } = await import(
    'amazon-cognito-identity-js'
  );
  const pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId });
  // The username is not in our stored blob, but Cognito's SDK only needs SOME username to build the
  // request — it takes the real identity from the refresh token itself. `sub` from the id token is
  // used because it is the stable identifier and is already available without another round trip.
  const sub = decodeClaims(tokens.idToken)?.sub;
  if (!sub) return null;
  const user = new CognitoUser({ Username: sub, Pool: pool });
  const rt = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });
  return new Promise((resolve) => {
    user.refreshSession(rt, (err, session) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        // Cognito may or may not rotate the refresh token; keep the old one when it does not, or the
        // next refresh would have nothing to present and the month-long session would end in an hour.
        refreshToken: session.getRefreshToken()?.getToken() ?? tokens.refreshToken,
      });
    });
  });
}

export async function signIn(
  cfg: AuthConfig,
  username: string,
  password: string,
): Promise<SignInResult> {
  if (!configured(cfg)) {
    return { kind: 'error', message: 'this build has no Cognito pool compiled in' };
  }
  const { CognitoUserPool, CognitoUser, AuthenticationDetails } = await import(
    'amazon-cognito-identity-js'
  );
  const pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.clientId });
  const user = new CognitoUser({ Username: username, Pool: pool });
  const auth = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise<SignInResult>((resolve) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        resolve({
          kind: 'ok',
          tokens: {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken()?.getToken(),
          },
        });
      },
      onFailure: (err) => {
        // `preventUserExistenceErrors: ENABLED` on the client means Cognito returns
        // NotAuthorizedException for both a wrong password and an unknown user. Passing
        // the message through unchanged preserves that indistinguishability rather than
        // helpfully leaking which one it was.
        resolve({ kind: 'error', message: String(err?.message ?? err) });
      },
      // The SDK invokes this with the secret code already associated. Resolving with a `complete`
      // closure keeps the SAME pattern as newPasswordRequired: the caller collects the code from
      // the user and calls back, so `auth.ts` stays free of DOM.
      totpRequired: () => {
        resolve({
          kind: 'totpRequired',
          complete: (code: string) =>
            new Promise<Tokens>((ok, fail) => {
              user.sendMFACode(
                code.trim(),
                {
                  onSuccess: (session) =>
                    ok({
                      idToken: session.getIdToken().getJwtToken(),
                      accessToken: session.getAccessToken().getJwtToken(),
                      refreshToken: session.getRefreshToken()?.getToken(),
                    }),
                  onFailure: (err) => fail(new Error(String(err?.message ?? err))),
                },
                // 'SOFTWARE_TOKEN_MFA', not 'SMS_MFA': the pool enables otp only
                // (`mfaSecondFactor: { sms: false, otp: true }`). Passing the wrong one produces a
                // confusing NotAuthorizedException rather than a clear rejection.
                'SOFTWARE_TOKEN_MFA',
              );
            }),
        });
      },
      mfaSetup: () => {
        // Reported, not silently handled. Enrolling mid-login would need the QR flow inside the
        // login form, and a user forced to enrol at a password prompt has no way to verify they are
        // scanning the right pool. The message tells them what to do; the profile screen does it.
        resolve({
          kind: 'mfaSetupRequired',
          message:
            'This account needs an authenticator app before signing in. ' +
            'Ask the administrator to set MFA to optional, enrol from the profile menu, then re-enable it.',
        });
      },
      newPasswordRequired: () => {
        resolve({
          kind: 'newPasswordRequired',
          complete: (newPassword: string) =>
            new Promise<Tokens>((ok, fail) => {
              // The second argument must be `{}`, not the attributes Cognito echoed back:
              // resubmitting `email_verified` (an immutable attribute on this pool) makes
              // the call fail with an InvalidParameterException that reads like a password
              // problem.
              user.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: (session) =>
                  ok({
                    idToken: session.getIdToken().getJwtToken(),
                    accessToken: session.getAccessToken().getJwtToken(),
                    refreshToken: session.getRefreshToken()?.getToken(),
                  }),
                onFailure: (err) => fail(new Error(String(err?.message ?? err))),
              });
            }),
        });
      },
    });
  });
}
